// Shared conversation-domain helpers for admin and embed surfaces.

export function canCancel(runState) {
  return runState === "queued" || runState === "running";
}

export function parseEventPayload(raw) {
  try {
    return JSON.parse(raw || "{}");
  } catch (_error) {
    return {};
  }
}

export function decisionKindLabel(kind) {
  switch (String(kind || "").trim()) {
  case "permission":
    return "Permission";
  case "question":
    return "Question";
  case "choice":
    return "Choice";
  case "confirm":
    return "Confirmation";
  case "text_input":
    return "Text input";
  default:
    return kind || "Decision";
  }
}

function decisionIsResuming(item, lookupTurn) {
  if (String(item?.state || "").trim() !== "resolved") {
    return false;
  }
  const turnItem = typeof lookupTurn === "function" ? lookupTurn(String(item?.turn_id || "")) : null;
  if (!turnItem) {
    return false;
  }
  if (String(turnItem.wait_state || "").trim() === "awaiting_decision") {
    return false;
  }
  return canCancel(turnItem.state);
}

export function decisionStatusLabel(item, lookupTurn = null) {
  if (decisionIsResuming(item, lookupTurn)) {
    return "Resuming";
  }
  switch (String(item?.state || "").trim()) {
  case "pending":
    return "Waiting";
  case "resolved":
    return item.response_action === "approve" ? "Approved" : "Resolved";
  case "rejected":
    return "Denied";
  case "canceled":
    return "Canceled";
  case "expired":
    return "Expired";
  case "failed":
    return "Failed";
  default:
    return item?.state || "Decision";
  }
}

export function decisionResolutionText(item, lookupTurn = null, options = {}) {
  if (decisionIsResuming(item, lookupTurn)) {
    return "Answer recorded. Turn is resuming.";
  }
  const action = String(item?.response_action || "").trim();
  const by = String(item?.resolved_by || "").trim();
  const formatTimestamp = typeof options.formatTimestamp === "function" ? options.formatTimestamp : defaultFormatTimestamp;
  const at = item?.resolved_at ? formatTimestamp(item.resolved_at) : "";
  const fragments = [];
  if (action) {
    fragments.push(`Action: ${action}`);
  }
  if (by) {
    fragments.push(`by ${by}`);
  }
  if (at && at !== "n/a") {
    fragments.push(`at ${at}`);
  }
  return fragments.length > 0 ? fragments.join(" ") : "Decision completed.";
}

function defaultFormatTimestamp(value) {
  if (!value) {
    return "n/a";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
}

export function normalizeDecisionArray(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  return [];
}

export function normalizePromotion(item) {
  return {
    ...item,
    id: String(item?.id || ""),
    thread_id: String(item?.thread_id || ""),
    workspace_id: String(item?.workspace_id || ""),
    mode: String(item?.mode || "local_merge"),
    target_branch: String(item?.target_branch || "main"),
    state: String(item?.state || "queued"),
    backend_state: String(item?.backend_state || ""),
    conflict_summary: String(item?.conflict_summary || ""),
    merge_commit: String(item?.merge_commit || ""),
    remote_review_url: String(item?.remote_review_url || "")
  };
}

export function normalizeThread(item) {
  return {
    ...item,
    mode: String(item?.mode || "harness"),
    agent_id: String(item?.agent_id || ""),
    workspace_id: String(item?.workspace_id || ""),
    adapter_id: String(item?.adapter_id || ""),
    state: String(item?.state || "open"),
    merge_state: String(item?.merge_state || ""),
    mergeable: Boolean(item?.mergeable),
    draft_branch: String(item?.draft_branch || ""),
    last_turn_state: String(item?.last_turn_state || ""),
    last_user_message: String(item?.last_user_message || ""),
    last_head_commit: String(item?.last_head_commit || ""),
    last_base_ref: String(item?.last_base_ref || ""),
    last_turn_id: String(item?.last_turn_id || item?.last_run_id || ""),
    last_promotion_id: String(item?.last_promotion_id || ""),
    last_request_id: String(item?.last_request_id || ""),
    capability_resolution: normalizeThreadCapabilityResolutionMap(item?.capability_resolution || {}),
    pinned_context_refs: Array.isArray(item?.pinned_context_refs) ? item.pinned_context_refs.map(normalizeThreadContextRef) : [],
    recovery_hint: String(item?.recovery_hint || ""),
    latest_promotion: item?.latest_promotion ? normalizePromotion(item.latest_promotion) : null,
    promotion_mode_options: Array.isArray(item?.promotion_mode_options) ? item.promotion_mode_options : ["local_merge"]
  };
}

export function normalizeTurn(item) {
  return {
    ...item,
    mode: String(item?.mode || "harness"),
    agent_id: String(item?.agent_id || ""),
    workspace_id: String(item?.workspace_id || ""),
    prompt: String(item?.text_input_preview || item?.user_message || item?.prompt || ""),
    assistant_message: String(item?.text_output_preview || item?.assistant_message || ""),
    capability_family: String(item?.capability_family || "responses"),
    input_parts: Array.isArray(item?.input_parts) ? item.input_parts.map(normalizeTurnInputPart) : [],
    output_parts: Array.isArray(item?.output_parts) ? item.output_parts.map(normalizeTurnOutputPart) : [],
    replay_mode: String(item?.replay_mode || ""),
    text_input_preview: String(item?.text_input_preview || item?.user_message || ""),
    text_output_preview: String(item?.text_output_preview || item?.assistant_message || ""),
    thread_id: String(item?.thread_id || item?.session_id || ""),
    wait_state: String(item?.wait_state || ""),
    pending_decision_id: String(item?.pending_decision_id || ""),
    pending_decision_kind: String(item?.pending_decision_kind || ""),
    pending_decision_summary: String(item?.pending_decision_summary || ""),
    model: String(item?.model || ""),
    route_id: String(item?.route_id || ""),
    request_log_id: String(item?.request_log_id || "")
  };
}

export function normalizeThreadAttachment(item) {
  return {
    ...item,
    id: String(item?.id || ""),
    thread_id: String(item?.thread_id || ""),
    turn_id: String(item?.turn_id || ""),
    origin: String(item?.origin || "upload"),
    media_kind: String(item?.media_kind || ""),
    content_type: String(item?.content_type || ""),
    filename: String(item?.filename || ""),
    size_bytes: Number(item?.size_bytes || 0),
    state: String(item?.state || "draft"),
    content_url: String(item?.content_url || ""),
    object_url: String(item?.object_url || "")
  };
}

export function normalizeTurnInputPart(item) {
  return {
    ...item,
    type: String(item?.type || ""),
    text: String(item?.text || ""),
    attachment_id: String(item?.attachment_id || ""),
    attachment: item?.attachment ? normalizeThreadAttachment(item.attachment) : null
  };
}

export function normalizeTurnOutputPart(item) {
  return {
    ...item,
    type: String(item?.type || ""),
    text: String(item?.text || ""),
    attachment_id: String(item?.attachment_id || ""),
    attachment: item?.attachment ? normalizeThreadAttachment(item.attachment) : null,
    language: String(item?.language || ""),
    response_format: String(item?.response_format || ""),
    revised_prompt: String(item?.revised_prompt || "")
  };
}

export function normalizeThreadContextRef(item) {
  return {
    ...item,
    id: String(item?.id || ""),
    type: String(item?.type || ""),
    attachment_id: String(item?.attachment_id || ""),
    turn_id: String(item?.turn_id || ""),
    output_index: Number(item?.output_index || 0),
    attachment: item?.attachment ? normalizeThreadAttachment(item.attachment) : null
  };
}

export function normalizeThreadCapabilityResolutionMap(value) {
  const out = {};
  if (!value || typeof value !== "object") {
    return out;
  }
  for (const [capability, raw] of Object.entries(value)) {
    out[String(capability || "").trim()] = {
      status: String(raw?.status || ""),
      route_id: String(raw?.route_id || ""),
      route_name: String(raw?.route_name || ""),
      model: String(raw?.model || ""),
      reason: String(raw?.reason || "")
    };
  }
  return out;
}

export function normalizeDecision(item) {
  return {
    ...item,
    kind: String(item?.kind || ""),
    state: String(item?.state || ""),
    summary: String(item?.summary || ""),
    prompt: String(item?.prompt || ""),
    detail: String(item?.detail || ""),
    thread_id: String(item?.thread_id || ""),
    turn_id: String(item?.turn_id || ""),
    response_action: String(item?.response_action || ""),
    resolved_by: String(item?.resolved_by || ""),
    fields: normalizeDecisionArray(item?.fields),
    actions: normalizeDecisionArray(item?.actions)
  };
}

export function sortThreads(items) {
  return [...items].sort((left, right) => new Date(right.updated_at || 0) - new Date(left.updated_at || 0));
}

export function sortTurnsDescending(items) {
  return [...items].sort((left, right) => new Date(right.created_at || 0) - new Date(left.created_at || 0));
}

export function sortTurnsAscending(items) {
  return [...items].sort((left, right) => new Date(left.created_at || 0) - new Date(right.created_at || 0));
}

export function sortDecisionsAscending(items) {
  return [...items].sort((left, right) => new Date(left.created_at || 0) - new Date(right.created_at || 0));
}

export function assistantTextFromEvents(events) {
  const deltas = events
    .filter((item) => item.event_type === "turn.output.delta")
    .map((item) => parseEventPayload(item.payload_json))
    .filter((payload) => payload && payload.role === "assistant" && typeof payload.delta === "string")
    .map((payload) => payload.delta);
  if (deltas.length > 0) {
    return deltas.join("");
  }
  const segments = events
    .filter((item) => item.event_type === "turn.message")
    .map((item) => parseEventPayload(item.payload_json))
    .filter((payload) => payload && payload.role === "assistant" && typeof payload.text === "string")
    .map((payload) => payload.text.trim())
    .filter(Boolean);
  if (segments.length === 0) {
    return "";
  }
  let merged = "";
  // Some harnesses emit progressively fuller assistant snapshots instead of clean deltas.
  // Keep the longest prefix-compatible segment and only append genuinely new trailing text.
  for (const segment of segments) {
    if (!merged) {
      merged = segment;
      continue;
    }
    if (segment === merged) {
      continue;
    }
    if (segment.startsWith(merged)) {
      merged = segment;
      continue;
    }
    if (merged.endsWith(segment)) {
      continue;
    }
    merged += segment;
  }
  return merged.trim();
}

export function threadSummaryFromEvents(events) {
  const completed = [...events].reverse().find((item) => item.event_type === "turn.completed");
  if (!completed) {
    return "";
  }
  const payload = parseEventPayload(completed.payload_json);
  return typeof payload.summary === "string" ? payload.summary.trim() : "";
}

export function threadWarningFromRun(currentRun, events) {
  if (currentRun.state !== "applied_with_warning") {
    return "";
  }
  const completed = [...events].reverse().find((item) => item.event_type === "turn.completed");
  if (completed) {
    const payload = parseEventPayload(completed.payload_json);
    if (typeof payload.warning === "string" && payload.warning.trim()) {
      return payload.warning.trim();
    }
  }
  return typeof currentRun.error_message === "string" ? currentRun.error_message.trim() : "";
}

export function threadErrorFromRun(currentRun, events) {
  if (currentRun.state === "applied_with_warning") {
    return "";
  }
  if (currentRun.error_message) {
    return currentRun.error_message;
  }
  const failed = [...events].reverse().find((item) => item.event_type === "turn.failed" || item.event_type === "turn.canceled");
  if (!failed) {
    return "";
  }
  const payload = parseEventPayload(failed.payload_json);
  return typeof payload.error === "string" ? payload.error.trim() : typeof payload.message === "string" ? payload.message.trim() : "";
}

export function runReadableResult(currentRun, events) {
  const assistantText = assistantTextFromEvents(events);
  if (assistantText) {
    return assistantText;
  }
  if (String(currentRun.wait_state || "") === "awaiting_decision") {
    return "Waiting for your decision...";
  }
  const summary = threadSummaryFromEvents(events);
  if (currentRun.state === "applied_with_warning") {
    if (summary) {
      return summary;
    }
    return "Changes applied, but the harness did not close the turn cleanly.";
  }
  const errorText = threadErrorFromRun(currentRun, events);
  if (errorText) {
    return errorText;
  }
  if (summary && summary.toLowerCase() !== "opencode turn completed") {
    return summary;
  }
  if (currentRun.state === "running") {
    return "Streaming response...";
  }
  if (summary) {
    return "Completed, but no assistant text was captured.";
  }
  return "No result available yet.";
}

export function activityEntriesFromEvents(events, currentRun = null, options = {}) {
  const items = [];
  let hasDecisionActivity = false;
  for (const item of events || []) {
    const entry = activityEntryFromEvent(item);
    if (!entry) {
      continue;
    }
    if (entry.kind === "decision") {
      hasDecisionActivity = true;
    }
    items.push(entry);
  }
  if (String(currentRun?.wait_state || "") === "awaiting_decision" && !hasDecisionActivity) {
    items.push({
      kind: "decision",
      tone: "warn",
      title: "Waiting for decision",
      detail: String(currentRun?.pending_decision_summary || "Human input is required before the turn can continue.").trim(),
      tier: 1,
      timestamp: null,
      eventType: "turn.decision.requested"
    });
  }
  const limit = Number(options.limit || 0);
  if (limit > 0 && items.length > limit) {
    return items.slice(-limit);
  }
  return items;
}

export function activityEntryFromEvent(item) {
  const eventType = String(item?.event_type || "").trim();
  if (!eventType) {
    return null;
  }
  const payload = parseEventPayload(item?.payload_json || "");
  const timestamp = item.created_at || null;
  switch (eventType) {
  case "turn.progress":
    return addTimestamp(progressActivityEntry(payload), timestamp);
  case "turn.tool_call":
    return addTimestamp(toolCallActivityEntry(payload), timestamp);
  case "turn.tool_result":
    return addTimestamp(toolResultActivityEntry(payload), timestamp);
  case "turn.decision.requested":
    return {
      kind: "decision",
      tone: "warn",
      title: "Waiting for decision",
      detail: firstNonEmpty(payload.summary, payload.prompt, payload.kind, "Human input is required."),
      tier: 1,
      timestamp,
      eventType
    };
  case "turn.decision.updated":
    return {
      kind: "decision",
      tone: "info",
      title: "Decision updated",
      detail: firstNonEmpty(payload.summary, payload.state, "Decision state changed."),
      tier: 1,
      timestamp,
      eventType
    };
  case "turn.decision.resolved":
    return {
      kind: "decision",
      tone: "ok",
      title: "Decision resolved",
      detail: firstNonEmpty(payload.response_action, payload.summary, "The turn can continue."),
      tier: 1,
      timestamp,
      eventType
    };
  case "turn.decision.expired":
    return {
      kind: "decision",
      tone: "error",
      title: "Decision expired",
      detail: firstNonEmpty(payload.summary, "The turn timed out waiting for input."),
      tier: 1,
      timestamp,
      eventType
    };
  case "turn.decision.failed":
    return {
      kind: "decision",
      tone: "error",
      title: "Decision failed",
      detail: firstNonEmpty(payload.summary, "The turn could not resolve the requested input."),
      tier: 1,
      timestamp,
      eventType
    };
  case "turn.trace":
    return addTimestamp(runtimeTraceActivityEntry(payload, eventType), timestamp);
  case "turn.failed":
    return {
      kind: "failure",
      tone: "error",
      title: "Turn failed",
      detail: firstNonEmpty(payload.error, payload.message, "The turn exited with an error."),
      tier: 1,
      timestamp,
      eventType
    };
  case "turn.canceled":
    return {
      kind: "failure",
      tone: "warn",
      title: "Turn canceled",
      detail: firstNonEmpty(payload.message, "The turn was canceled before completion."),
      tier: 1,
      timestamp,
      eventType
    };
  case "turn.completed":
    if (!payload.applied) {
      return null;
    }
    return {
      kind: "state",
      tone: "warn",
      title: "Turn finished with warning",
      detail: firstNonEmpty(payload.warning, payload.summary, "Changes were applied, but the harness did not close cleanly."),
      tier: 2,
      timestamp,
      eventType
    };
  default:
    return null;
  }
}

export function failureSummaryFromTurn(currentRun, events, diagnostics = null) {
  const failurePhase = String(diagnostics?.stream?.failure_phase || "").trim();
  switch (failurePhase) {
  case "watchdog_timeout":
    return "The provider stopped emitting events before the turn reached a terminal state.";
  case "stream_closed_before_completion":
    return "The provider stream closed before the turn completed.";
  case "provider_terminal_error":
    return "The provider reported a terminal error while the turn was running.";
  case "ambiguous_cancel":
    return "The turn was canceled before the provider reached a clear terminal state.";
  case "stream_connect":
    return "The runtime failed before the provider event stream connected.";
  case "request_post":
    return "The runtime could not post the async request cleanly.";
  case "pre_provider_event":
    return "The async request was posted, but no provider events were observed.";
  case "post_provider_activity":
    return "Provider activity was observed, but the turn still failed before completion.";
  default:
    return threadErrorFromRun(currentRun || {}, events);
  }
}

function progressActivityEntry(payload) {
  const stage = firstNonEmpty(payload.stage, "progress");
  const message = firstNonEmpty(payload.message, payload.text, stage);
  const status = String(payload.status || "").trim();
  return {
    kind: "progress",
    tone: status === "completed" ? "ok" : "info",
    title: status === "completed" ? `${capitalize(stage)} complete` : capitalize(stage),
    detail: message,
    tier: 2,
    eventType: "turn.progress"
  };
}

function toolCallActivityEntry(payload) {
  const name = firstNonEmpty(payload.name, "Tool");
  return {
    kind: "tool",
    tone: "accent",
    title: `Running ${name}`,
    detail: summarizeToolDetail(payload.input, "Waiting for tool result."),
    tier: 1,
    eventType: "turn.tool_call"
  };
}

function toolResultActivityEntry(payload) {
  const name = firstNonEmpty(payload.name, "Tool");
  const hasError = payload.error != null && String(payload.error).trim() !== "";
  return {
    kind: "tool",
    tone: hasError ? "error" : "ok",
    title: hasError ? `${name} failed` : `${name} completed`,
    detail: hasError ? summarizeToolDetail(payload.error, "Tool returned an error.") : summarizeToolDetail(payload.result, "Tool completed."),
    tier: 1,
    eventType: "turn.tool_result"
  };
}

function runtimeTraceActivityEntry(payload, eventType) {
  if (String(payload?.provider || "").trim() !== "lytebulb.runtime") {
    return null;
  }
  switch (String(payload?.event_type || "").trim()) {
  case "stream.connected":
    return null;
  case "stream.request.posted":
    return null;
  case "stream.first_provider_event":
    return null;
  case "stream.terminal_state":
    if (payload.terminal_state !== "error") {
      return null;
    }
    return {
      kind: "stream",
      tone: "error",
      title: "Agent stopped responding",
      detail: firstNonEmpty(payload.error, "The provider reported a terminal error."),
      tier: 1,
      eventType
    };
  case "stream.watchdog_timeout":
    return {
      kind: "failure",
      tone: "error",
      title: "Agent stopped responding",
      detail: "The watchdog fired before the provider reached a terminal state.",
      tier: 1,
      eventType
    };
  case "stream.closed_before_completion":
    return {
      kind: "failure",
      tone: "error",
      title: "Stream closed early",
      detail: "The event stream closed before the provider completed the turn.",
      tier: 1,
      eventType
    };
  default:
    return null;
  }
}

function addTimestamp(entry, timestamp) {
  if (!entry) {
    return null;
  }
  entry.timestamp = timestamp;
  return entry;
}

export function activityEntriesForDisplay(events, currentRun = null, opts = {}) {
  const all = activityEntriesFromEvents(events, currentRun, opts);
  const maxTier = opts.maxTier || 2;
  return all.filter((entry) => entry.tier <= maxTier);
}

export function compactActivitySummary(entries) {
  let toolCount = 0;
  let decisionCount = 0;
  let stepCount = 0;
  let minTs = null;
  let maxTs = null;
  for (const entry of entries) {
    stepCount++;
    if (entry.kind === "tool") toolCount++;
    if (entry.kind === "decision") decisionCount++;
    if (entry.timestamp) {
      const t = new Date(entry.timestamp).getTime();
      if (!Number.isNaN(t)) {
        if (minTs === null || t < minTs) minTs = t;
        if (maxTs === null || t > maxTs) maxTs = t;
      }
    }
  }
  return {
    toolCount,
    decisionCount,
    stepCount,
    durationMs: minTs !== null && maxTs !== null ? maxTs - minTs : 0
  };
}

export function relativeTime(timestamp, referenceTimestamp) {
  if (!timestamp || !referenceTimestamp) return "";
  const ms = new Date(timestamp).getTime() - new Date(referenceTimestamp).getTime();
  if (Number.isNaN(ms) || ms < 0) return "";
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds >= 300) return "5m+";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m${seconds > 0 ? String(seconds).padStart(2, "0") + "s" : ""}`;
  return `${seconds}s`;
}

function summarizeToolDetail(value, fallback) {
  if (value == null) {
    return fallback;
  }
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  try {
    const encoded = JSON.stringify(value);
    if (!encoded || encoded === "{}" || encoded === "[]") {
      return fallback;
    }
    return encoded.length > 140 ? `${encoded.slice(0, 137)}...` : encoded;
  } catch (_error) {
    return fallback;
  }
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

function capitalize(value) {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "";
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function runStateFromEvent(event) {
  const eventType = String(event?.event_type || event || "").trim();
  if (eventType === "turn.completed") {
    const payload = parseEventPayload(event?.payload_json || "");
    return payload?.applied ? "applied_with_warning" : "succeeded";
  }
  switch (eventType) {
  case "turn.started":
    return "running";
  case "turn.failed":
    return "failed";
  case "turn.canceled":
    return "canceled";
  default:
    return "";
  }
}

export function resolveRunState(currentState, nextState) {
  const current = String(currentState || "").trim();
  const next = String(nextState || "").trim();
  if (!next) {
    return current;
  }
  if (!current) {
    return next;
  }
  if (isTerminalRunState(current) && !isTerminalRunState(next)) {
    return current;
  }
  if (current === "running" && next === "queued") {
    return current;
  }
  return next;
}

export function isTerminalRunState(value) {
  return value === "succeeded" || value === "applied_with_warning" || value === "failed" || value === "canceled";
}
